import React, { Component } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSyncAlt, faTimes } from "@fortawesome/free-solid-svg-icons";
import {
  ControlBarContainer,
  ControlList,
  ControlListItem
} from "../../styled-components/ControlBar";
import { SearchBox, IconButton } from "../../styled-components/";
import SortOption from "../../constants/sortOptions";
import { actions as savedItemsActions } from "../../actions/savedItems";
import LuciPropTypes from "../../propTypes/";
import MultiSelect from "../../styled-components/MultiSelect";
import { generateShortcutText } from "../../util/keyShortcutUtils";
import localize from "../../util/localizeUtils";
import ConfirmDialog from "../ConfirmDialog";

const Mousetrap = window.Mousetrap;

const ControlListPadded = styled(ControlList)`
  padding: 0 0.5rem;
`;

const MenuButton = styled(IconButton)`
  margin-left: 1rem;
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
`;

const DeleteIcon = styled.div`
  color: ${props => props.theme.colors.red};
`;

const RefreshIcon = styled.div`
  color: ${props => props.theme.colors.secondary};
`;

class ControlBar extends Component {
  constructor() {
    super();

    this.state = {
      searchTerms: "",
      confirmDialogVisible: false
    };

    this.contextCombo = React.createRef();
  }

  componentDidMount() {
    this.bindShortcuts();
  }

  componentWillUnmount() {
    this.unbindShortcuts();
  }

  bindShortcuts = () => {
    Mousetrap.bindGlobal(this.props.shortcuts.openContextCombo, () => {
      if (this.contextCombo && this.contextCombo.current) {
        this.contextCombo.current.focus();
      }
    });
  };

  unbindShortcuts = () => {
    Mousetrap.unbind(this.props.shortcuts.openContextCombo);
  };

  // Handles both the onChange and onKeyPress events.
  onSearch = event => {
    if (event.key === "Enter") {
      this.props.changeSearchTerms(this.state.searchTerms);
      event.preventDefault(); // This prevents the "onChange" from being fired.
    } else if (!event.key) {
      this.setState({ searchTerms: event.target.value });
      if (event.target.value === "") {
        this.props.changeSearchTerms("");
      }
    }
  };

  confirmDelete = () => {
    this.setState({ confirmDialogVisible: true });
  };

  closeConfirm = () => {
    this.setState({ confirmDialogVisible: false });
  };

  deleteItem = () => {
    this.closeConfirm();
    this.props.deleteSavedItem(this.props.selectedItemId);
  };

  render() {
    const {
      contextList,
      contextId,
      isModifierActive,
      sortBy,
      selectedItemId,
      reload
    } = this.props;
    // need to make a copy or assets.contextList will also add Show all option
    const contexts = [...contextList];
    if (!contexts.some(c => c.id === -1)) {
      contexts.unshift({ id: -1, name: localize("showAllOption") });
    }
    return (
      <ControlBarContainer>
        <ConfirmDialog
          visible={this.state.confirmDialogVisible}
          content={localize("confirmDeleteSavedItemText")}
          title={localize("deleteSavedItemDialogTitle")}
          onConfirm={() => this.deleteItem()}
          onClose={() => this.closeConfirm()}
        />
        <ControlListPadded>
          <ControlListItem>
            <SearchBox
              borderColor={this.state.searchTerms.length > 0 ? "#3399FF" : null}
              ref={r => {
                this.searchBox = r;
              }}
              type="search"
              id="search-terms"
              width="15rem"
              onKeyPress={this.onSearch}
              onChange={this.onSearch}
              value={this.state.searchTerms}
              placeholder={localize("searchPlaceholder")}
            />
          </ControlListItem>
          <ControlListItem>
            <MultiSelect
              ref={this.contextCombo}
              inlineLabel={generateShortcutText(
                localize("contextLabel"),
                isModifierActive,
                true
              )}
              options={contexts}
              selectedValue={contextId}
              single
              onChange={value => this.props.changeContext(value)}
            />
          </ControlListItem>
          <ControlListItem>
            <MultiSelect
              inlineLabel={localize("sortLabel")}
              options={[
                { id: SortOption.A_Z, name: localize("sortByNameAscOption") },
                { id: SortOption.Z_A, name: localize("sortByNameDescOption") },
                {
                  id: SortOption.MOST_RECENT,
                  name: localize("sortByDateDescOption")
                },
                {
                  id: SortOption.LEAST_RECENT,
                  name: localize("sortByDateAscOption")
                }
              ]}
              selectedValue={sortBy}
              single
              onChange={value => this.props.changeSortBy(value)}
            />
          </ControlListItem>
        </ControlListPadded>
        <ControlListPadded>
          <ControlListItem>
            <MenuButton onClick={reload}>
              <RefreshIcon>
                <FontAwesomeIcon fixedWidth icon={faSyncAlt} size="lg" />
              </RefreshIcon>
            </MenuButton>
            <MenuButton
              onClick={() => this.confirmDelete()}
              disabled={!selectedItemId}
            >
              <DeleteIcon>
                <FontAwesomeIcon fixedWidth icon={faTimes} size="lg" />
              </DeleteIcon>
            </MenuButton>
          </ControlListItem>
        </ControlListPadded>
      </ControlBarContainer>
    );
  }
}

ControlBar.defaultProps = {
  sortBy: "",
  selectedItemId: null
};

ControlBar.propTypes = {
  changeContext: PropTypes.func.isRequired,
  contextId: PropTypes.number.isRequired,
  isModifierActive: PropTypes.bool.isRequired,
  shortcuts: PropTypes.shape({
    openContextCombo: PropTypes.arrayOf(PropTypes.string),
    openFilterCombo: PropTypes.arrayOf(PropTypes.string),
    focusSearchBar: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  sortBy: PropTypes.string,
  contextList: LuciPropTypes.ContextList.isRequired,
  changeSortBy: PropTypes.func.isRequired,
  selectedItemId: PropTypes.number,
  deleteSavedItem: PropTypes.func.isRequired,
  reload: PropTypes.func.isRequired,
  changeSearchTerms: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  contextList: state.assets.contextList,
  contextId: state.savedItems.contextId,
  isModifierActive: state.keyShortcuts.isModifierActive,
  shortcuts: state.keyShortcuts.shortcuts,
  sortBy: state.savedItems.sortBy
});

const mapActionHandlers = {
  changeContext: savedItemsActions.changeContext,
  changeSortBy: savedItemsActions.changeSortBy,
  deleteSavedItem: savedItemsActions.deleteSavedItem,
  reload: savedItemsActions.reload,
  changeSearchTerms: savedItemsActions.changeSearchTerms
};

export default connect(mapStateToProps, mapActionHandlers)(ControlBar);



// WEBPACK FOOTER //
// ./app/components/SavedItems/ControlBar.js